import * as GC from '@grapecity/spread-sheets';
import * as SheetFunctions from '@helpers/spread/sheet-functions';
import { ResultItem, ResultsetItem } from '@models/resultset-item';
import { ActiveSummary, MyDateTime, ActiveSummarySelection } from '@models/results/results-class-library';
import { setDefaultBookOptions, setDefaultSheetOptions } from '@shared/spreadsheet/base';
import {
  resultsSpreadConfig,
  ROW_HEIGHT,
  COL_WIDTH,
  ROW_COUNT,
  ROW_HEIGHT_TITLE,
  INVALID_PERIOD_ZERO_DATE_MESSAGE,
  FAIL_COLOR,
} from './results-spread-config';
import { SpreadContextMenu } from '@shared/spreadsheet/protection';
import { getFont } from '@shared/spreadsheet/spreadsheet-config';
import { addLiquidityToSheet } from './liquidities-builder';
import * as SPREAD_CONFIG_LIQSRC from '@components/model-structure/liquidity-sources/liquidity-sources.spread.config';
import { addExogenousByGroupID } from './exogenous-builder';
import { ResultsStoreService } from '@services/results/results-store.service';
import { SettingsService } from '@services/settings/settings.service';
import { Result } from '@models/result';

let asOfDate;
let currentRow: number;
let spread: GC.Spread.Sheets.Workbook;
let settingsService: SettingsService;
let resultsStore: ResultsStoreService;

export function createSummaryForResult(
  workbook: GC.Spread.Sheets.Workbook,
  workSheet: GC.Spread.Sheets.Worksheet,
  result,
  component
): void {
  // Injecting required elements from parent component
  settingsService = component.settingsService;
  resultsStore = component.resultsStore;
  spread = workbook;
  asOfDate = component.asOfDate;

  workSheet.suspendPaint();
  workSheet.suspendCalcService();

  const startColumn = 1;
  currentRow = 0;
  let cell;
  let startRow; // start row for grouping range
  let groupingRowCount; // end row for grouping range

  // Events
  // ===============================
  workSheet.bind(GC.Spread.Sheets.Events.TopRowChanged, function (sender, args) {
    ActiveSummary.row = args.newTopRow;
    ActiveSummary.last_change = MyDateTime.getCurrentDateTime();
    // console.log('activeSummaryRow', ActiveSummary.row, ActiveSummary.last_change);
  });

  workSheet.bind(GC.Spread.Sheets.Events.CellClick, function (e, info) {
    ActiveSummarySelection.row = info.row;
    ActiveSummarySelection.col = info.col;
    ActiveSummarySelection.last_change = MyDateTime.getCurrentDateTime();
    // console.log('activeSummarySelection', ActiveSummarySelection);
  });

  workSheet.bind(GC.Spread.Sheets.Events.CellDoubleClick, function (e, info) {
    ActiveSummarySelection.row = info.row;
    ActiveSummarySelection.col = info.col;
    ActiveSummarySelection.last_change = MyDateTime.getCurrentDateTime();
    // console.log('activeSummarySelection', ActiveSummarySelection);
  });

  // Options
  // ===============================
  setDefaultBookOptions(spread.options, resultsSpreadConfig.spreadOptions);
  setDefaultSheetOptions(workSheet.options, { ...resultsSpreadConfig.sheetOptions });
  workSheet.options.isProtected = true;
  workSheet.options.protectionOptions.allowResizeRows = true;
  workSheet.options.protectionOptions.allowResizeColumns = true;
  workSheet.options.protectionOptions.allowOutlineColumns = true;
  workSheet.options.protectionOptions.allowOutlineRows = true;

  settingsService.setSheetColor(workSheet);
  workSheet.defaults.rowHeight = ROW_HEIGHT;
  workSheet.defaults.colWidth = COL_WIDTH;
  SpreadContextMenu.disableClearContent(spread);
  workSheet.setColumnCount(5 + result.PeriodDates.length);
  workSheet.setRowCount(ROW_COUNT);

  // Make some column-width adjustements
  [0, 1].forEach(iCol => workSheet.setColumnWidth(iCol, 50));
  [2, 3].forEach(iCol => workSheet.setColumnWidth(iCol, 150));

  // Sheet Title
  currentRow++;
  cell = workSheet.getCell(currentRow, startColumn);
  workSheet.setRowHeight(currentRow, ROW_HEIGHT_TITLE, GC.Spread.Sheets.SheetArea.viewport);
  cell.hAlign(GC.Spread.Sheets.HorizontalAlign.left);
  cell.vAlign(GC.Spread.Sheets.VerticalAlign.center);
  cell.value(`Liquidity Stress - ${result.Mnemonic} Summary Report`).wordWrap(false);
  cell.font(getFont('xx-lg', true));

  // As Of date - title
  currentRow++;
  cell = workSheet.getCell(currentRow, startColumn);
  cell.value('As of:').wordWrap(false);
  cell.font(getFont('md'));
  SheetFunctions.centerAndWrapCell(spread, workSheet, currentRow, startColumn);
  cell.hAlign(GC.Spread.Sheets.HorizontalAlign.left);
  cell.vAlign(GC.Spread.Sheets.VerticalAlign.center);

  // As Of Date - date
  const dateColumn = startColumn + 1;
  cell = workSheet.getCell(currentRow, dateColumn);

  const date = asOfDate;
  const periodDateFailed = !asOfDate;
  if (periodDateFailed) {
    console.warn('Invalid period 0 date being passed as parameter to buildCoverPageSheet()', {
      asOfDate,
    });
    cell.value(INVALID_PERIOD_ZERO_DATE_MESSAGE).foreColor('red').backColor(FAIL_COLOR);
  } else {
    SheetFunctions.setOutputReportDate(spread, workSheet, cell, date as Date, true, null);
  }
  workSheet.addSpan(currentRow, dateColumn, 1, 3, GC.Spread.Sheets.SheetArea.viewport);
  cell.font(getFont('md', periodDateFailed)).wordWrap(false);

  SheetFunctions.centerAndWrapCell(spread, workSheet, currentRow, dateColumn);
  cell.hAlign(GC.Spread.Sheets.HorizontalAlign.left);
  // End of "As Of Date - date"

  currentRow++;
  // Period Dates
  const numPeriods = result.PeriodDates.length;

  for (let i = 1; i < numPeriods; i++) {
    const currentPeriodColumn = startColumn + 3 + i;
    cell = workSheet.getCell(currentRow, currentPeriodColumn);
    const dateSrcValue = result.PeriodDates[i];
    SheetFunctions.setOutputReportDate(spread, workSheet, cell, new Date(dateSrcValue), false, `[$-${SettingsService.locale}]mmm-yy;@`);
    // Fix 3777 for Summary View: Same column width for the periods
    workSheet.setColumnWidth(currentPeriodColumn, COL_WIDTH + 50, GC.Spread.Sheets.SheetArea.viewport);
  }

  currentRow++;
  // Build sources of Cash
  workSheet.setValue(currentRow, startColumn, 'Sources of Cash (Existing)');
  workSheet.getCell(currentRow, startColumn).font(getFont('sm', true));
  workSheet.getCell(currentRow, startColumn).textDecoration(GC.Spread.Sheets.TextDecorationType.underline);

  currentRow++;

  startRow = currentRow + 1;
  groupingRowCount = Object.keys(result.Summary).length;
  for (const key of Object.keys(result.Summary)) {
    const summary = result.Summary[key];

    if (summary.SummaryType === 1) {
      currentRow = displaySummaries(workSheet, result.Summary[key], currentRow, result.PeriodDates, resultsStore);
    }
  }
  currentRow++;

  // Build uses of Cash
  workSheet.setValue(currentRow, startColumn, 'Uses of Cash (Existing)');
  workSheet.getCell(currentRow, startColumn).font(getFont('sm', true));
  workSheet.getCell(currentRow, startColumn).textDecoration(GC.Spread.Sheets.TextDecorationType.underline);

  currentRow++;
  // startRow = currentRow + 1;
  for (const key of Object.keys(result.Summary)) {
    const summary = result.Summary[key];
    if (summary.SummaryType === 2) {
      currentRow = displaySummaries(workSheet, result.Summary[key], currentRow, result.PeriodDates, resultsStore);
    }
  }

  // Display total outflow
  // startRow = currentRow + 1;
  for (const key of Object.keys(result.Summary)) {
    const summary = result.Summary[key];
    if (summary.SummaryType === -1) {
      currentRow = displaySummaries(workSheet, result.Summary[key], currentRow, result.PeriodDates, resultsStore);
    }
  }

  currentRow++;

  // Build source Cash
  workSheet.setValue(currentRow, startColumn, 'New Business Activity');
  workSheet.getCell(currentRow, startColumn).font(getFont('sm', true));
  workSheet.getCell(currentRow, startColumn).textDecoration(GC.Spread.Sheets.TextDecorationType.underline);

  currentRow++;
  for (const key of Object.keys(result.Summary)) {
    if (result.Summary[key].SummaryType === 3) {
      currentRow = displaySummaries(workSheet, result.Summary[key], currentRow, result.PeriodDates, resultsStore);
    }
  }

  for (const key of Object.keys(result.Summary)) {
    if (result.Summary[key].SummaryType === -2) {
      currentRow = displaySummaries(workSheet, result.Summary[key], currentRow, result.PeriodDates, resultsStore);
    }
  }

  currentRow++;
  for (const key of Object.keys(result.Summary)) {
    if (result.Summary[key].SummaryType === 4) {
      currentRow = displaySummaries(workSheet, result.Summary[key], currentRow, result.PeriodDates, resultsStore);
    }
  }

  for (const key of Object.keys(result.Summary)) {
    if (result.Summary[key].SummaryType === -3) {
      currentRow = displaySummaries(workSheet, result.Summary[key], currentRow, result.PeriodDates, resultsStore);
    }
  }
  for (const key of Object.keys(result.Summary)) {
    if (result.Summary[key].SummaryType === -4) {
      currentRow = displaySummaries(workSheet, result.Summary[key], currentRow, result.PeriodDates, resultsStore);
    }
  }

  // Liquidity Sources in Summary View
  if (result.Liquidities && Object.keys(result.Liquidities).length > 0) {
    const acceptedTypes = SPREAD_CONFIG_LIQSRC.SpreadConfig.LIQ_SOURCES_ACCEPTED_TYPE_VALUES;
    currentRow++; // margin-top

    //  Primary, Secondary, Tertiary...
    acceptedTypes.forEach((typeName, index) => {
      const typeNum = index + 1;
      const newRow = addLiquidityToSheet(
        currentRow,
        spread,
        workSheet,
        result.Liquidities,
        typeNum,
        result.IgnoreErrors,
        result.Survival_Horizon,
        resultsStore,
        result.ResultSetID,
        result.Mnemonic
      );
      // margin between liq. types:
      if (index < acceptedTypes.length - 1) {
        currentRow = newRow + 1;
      } else {
        currentRow = newRow;
      }
    });

    currentRow++; // margin-bottom
  }

  // Build Summary-view KRIs table
  buildSummaryKRIs(workSheet, result, result.IgnoreErrors);

  // Delete unused column
  workSheet.deleteColumns(4, 1);

  // Freeze columns/rows
  workSheet.frozenRowCount(4);
  workSheet.frozenColumnCount(4);

  // Delete extra unused rows
  currentRow += 5;
  workSheet.deleteRows(currentRow - 1, workSheet.getRowCount() - currentRow);

  workSheet.resumeCalcService();
  workSheet.resumePaint();
}

function displaySummaries(
  workSheet: GC.Spread.Sheets.Worksheet,
  summary: any,
  row: number,
  PeriodDates: any,
  _resultsStore: ResultsStoreService
): number {
  if (!resultsStore) resultsStore = _resultsStore;

  const headerColumn = 1;
  const totalColumn = 2;
  const valueColumn = 5;
  let startRow = 0;
  const summaryHeaderFontSize = 'x-sm';
  const summaryTotalFontSize = 'x-sm';
  const childrenFontSize = 'x-sm';

  const isATotal = summary.Category.Name === 'Total';

  const isSummaryView = workSheet.name().toLowerCase().includes(`-summary`);

  if (isSummaryView && isATotal) {
    row++;
  }

  // ------------- ROW HEADERS ----------------

  if (summary.IsSummaryFinalTotal) {
    // Examples: "Total - Inflow (Outflow)" or "Cumulative - Inflow (Outflow)"
    row++;
    workSheet.getCell(row, headerColumn).value(summary.Name).font(getFont(summaryTotalFontSize, true));
    workSheet.getCell(row, headerColumn).hAlign(GC.Spread.Sheets.HorizontalAlign.left);

    for (let i = 0; i < 3 + PeriodDates.length; i++) {
      workSheet
        .getCell(row - 1, headerColumn + i, GC.Spread.Sheets.SheetArea.viewport)
        .borderBottom(new GC.Spread.Sheets.LineBorder('Black', GC.Spread.Sheets.LineStyle.thick));
      workSheet
        .getCell(row, headerColumn + i, GC.Spread.Sheets.SheetArea.viewport)
        .borderBottom(new GC.Spread.Sheets.LineBorder('Black', GC.Spread.Sheets.LineStyle.double));
    }
  }

  if (summary.IsSummaryHeaderTotal) {
    // Examples: "Existing Balance Sheet - Inflow (Outflow)" or "New Business - Inflow"
    row++;
    workSheet.setValue(row, headerColumn, summary.Name);
    SheetFunctions.centerAndWrapCell(spread, workSheet, row, headerColumn);
    workSheet
      .getCell(row, headerColumn)
      .font(getFont(summaryHeaderFontSize, true))
      .hAlign(GC.Spread.Sheets.HorizontalAlign.left)
      .vAlign(GC.Spread.Sheets.VerticalAlign.center)
      .wordWrap(false);

    // Top & bottom borders for summary header totals
    for (let i = 0; i < 3 + PeriodDates.length; i++) {
      workSheet
        .getCell(row - 1, headerColumn + i, GC.Spread.Sheets.SheetArea.viewport)
        .borderBottom(new GC.Spread.Sheets.LineBorder('Black', GC.Spread.Sheets.LineStyle.thin));
      workSheet
        .getCell(row, headerColumn + i, GC.Spread.Sheets.SheetArea.viewport)
        .borderBottom(new GC.Spread.Sheets.LineBorder('Black', GC.Spread.Sheets.LineStyle.double));
    }
  }

  if (summary.IsSummaryTotal) {
    // Level 1 children. Example: "Investment Cash Flows" or "Total Loan Rollover"
    const cell = workSheet.getCell(row, totalColumn);
    workSheet.setValue(row, totalColumn, summary.Name);
    // cell.wordWrap(true);
    // workSheet.addSpan(row, totalColumn, 1, 3, GC.Spread.Sheets.SheetArea.viewport);
    cell.font(getFont(summaryTotalFontSize, isATotal));
  }

  // ------------- VALUES ----------------

  // row Values
  let k = 0;
  for (const key of Object.keys(summary.Category.Properties)) {
    for (const res of Object.keys(summary.Category.Properties[key].Results)) {
      const val: number = Number(summary.Category.Properties[key].Results[res].Value);
      const rounding = true;
      const cell = workSheet.getCell(row, valueColumn + k);
      SheetFunctions.setOutputReportNumberFormatting(spread, workSheet, row, valueColumn + k, val, null, rounding);
      resultsStore.flagDisplayZeroAsForConversionOnExport(workSheet, cell);
      cell
        // Font overwrite
        .font(getFont(summaryTotalFontSize, isATotal))
        .hAlign(GC.Spread.Sheets.HorizontalAlign.right);
      k++;
    }
  }

  row++;
  startRow = row;
  // Level 2 Children. Example: "Total MBS & CMO", "Total Agency Bonds", ...

  if (summary && summary.ChildAccounts) {
    for (const key of Object.keys(summary.ChildAccounts)) {
      displayChildren(workSheet, summary.ChildAccounts[key], row, childrenFontSize);
      row++;
    }
    // Level 2 Grouping
    const groupingRowCount: number = Object.keys(summary.ChildAccounts).length;
    SheetFunctions.addRowRange(workSheet, startRow, groupingRowCount);
  }
  return row;
}

function buildSummaryKRIs(sheet: GC.Spread.Sheets.Worksheet, result: any, ignoreErrors: boolean) {
  // Building KRIs for the Summary view...
  const startingCol = 1;
  const colShift = 1;
  const enableDISFilter = true;
  currentRow = addExogenousByGroupID(
    currentRow,
    spread,
    sheet,
    result.Exogenous,
    ignoreErrors,
    startingCol,
    colShift,
    enableDISFilter,
    false,
    resultsStore,
    result.ResultSetID,
    result.Mnemonic
  );

  currentRow++;
}

function displayChildren(workSheet: GC.Spread.Sheets.Worksheet, child: any, row: number, fontSize: any) {
  const childColumn = 3;
  const valueColumn = 5;

  workSheet.setValue(row, childColumn, child.Name);
  workSheet
    .getCell(row, childColumn)
    // .wordWrap(true)
    .font(getFont(fontSize));
  workSheet.addSpan(row, childColumn, 1, 2, GC.Spread.Sheets.SheetArea.viewport);
  let k = 0;
  for (const key of Object.keys(child.Category.Properties)) {
    for (const res of Object.keys(child.Category.Properties[key].Results)) {
      // const val: number = Math.round(Math.abs(child.Category.Properties[key].Results[res].value)) | 0;
      const val = Number(child.Category.Properties[key].Results[res].Value);
      const rounding = true;
      const cell = workSheet.getCell(row, valueColumn + k);
      SheetFunctions.setOutputReportNumberFormatting(spread, workSheet, row, valueColumn + k, val, null, rounding);
      resultsStore.flagDisplayZeroAsForConversionOnExport(workSheet, cell);
      workSheet
        .getCell(row, valueColumn + k)
        .hAlign(GC.Spread.Sheets.HorizontalAlign.right)

        // Font overwrite
        .font(getFont('x-sm', false, false));
      k++;
    }
  }
}
